import React, { useCallback, useContext, useMemo, useState, useEffect } from 'react';

import { Skeleton } from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';

import Benefits from './sections/Benefits/Benefits';
import CollaboratorBalance from './sections/CollaboratorBalance/CollaboratorBalance';
import ContactData from './sections/ContactData/ContactData';
import ContractDetails from './sections/ContractDetails/ContractDetails';
import EditRfData from './sections/EditRfData/EditRfData';
import HealthProtection from './sections/HealthProtection/HealthProtection';
import CollaboratorsDetailHero from './sections/Hero/CollaboratorDetailsHero';
import BdsSegment from '../../shared/components/v3/BdsSegment/BdsSegment';
import { COLLABORATORS_HOME_ROUTE } from '../../shared/constants/auth';
import { COLLABORATOR_DETAIL_TABS } from '../../shared/constants/collaboratorDetails';
import { PLAN_TYPE_FR } from '../../shared/constants/plan';
import { CAN_SEE_COLLABORATOR_BALANCE_TAB_FG } from '../../shared/constants/statsig';
import { CLICK_ACTION, COLLABORATOR_TABS } from '../../shared/constants/tagManager';
import { CompanyContext } from '../../shared/context/company/companyContext';
import { PlansContext } from '../../shared/context/plans/plansContext';
import { useCollaboratorBenefits } from '../../shared/hooks/useCollaboratorBenefits/useCollaboratorBenefits';
import useCollaboratorPlayerData from '../../shared/hooks/useCollaboratorPlayerData/useCollaboratorPlayerData';
import useCollaboratorRFData from '../../shared/hooks/useCollaboratorRFData/useCollaboratorRFData';
import { useScrollState } from '../../shared/hooks/useScrollState/useScrollState';
import type { IMembersData } from '../../shared/interfaces/businessData';
import type { BenefitsState, ICategorizedBenefits } from '../../shared/interfaces/collaborators';
import TagManagerService from '../../shared/services/gtm/tagManagerService';

import './CollaboratorDetails.scss';

interface TabItem {
  title: string;
  type: string;
  category?: string;
}

const CollaboratorDetail = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [dynamicTabs, setDynamicTabs] = useState<TabItem[]>([]);

  const { t } = useTranslation();

  const isScrolled = useScrollState(0);

  const location = useLocation();
  const { collaborator } = (location.state as { collaborator?: IMembersData } | null) ?? {};
  const { companyData, isUSA } = useContext(CompanyContext);
  const { plans } = useContext(PlansContext);
  const { client } = useStatsigClient();

  const plan = collaborator?.plans[0];
  const currentPlan = plans?.find((p) => p.id === Number(plan));
  const planName = currentPlan?.name || '';
  const isRF = currentPlan?.type === PLAN_TYPE_FR;
  const currencySalary = currentPlan?.currency;
  const selectedTab = dynamicTabs[tabIndex];
  const canSeeBalanceTab = useMemo(() => client?.checkGate(CAN_SEE_COLLABORATOR_BALANCE_TAB_FG), [client]);

  const { allBenefitsState } = useCollaboratorBenefits({
    companyId: companyData.id,
    collaboratorEmail: collaborator?.email ?? '',
    enabled: !!collaborator
  });

  const { rfDataState, refetchData } = useCollaboratorRFData(companyData.id, collaborator?.email ?? '', isRF);

  const { playerDataState, refetchPlayerData } = useCollaboratorPlayerData(
    companyData.id,
    collaborator?.email ?? '',
    !!collaborator
  );

  useEffect(() => {
    if (!allBenefitsState.isLoading) {
      const filteredTabs = COLLABORATOR_DETAIL_TABS.filter((tab) => {
        const category = tab.type;

        if (category === 'contact') {
          return true;
        }

        if (category === 'contract') {
          return Boolean(isUSA);
        }

        if (isRF && category === PLAN_TYPE_FR) {
          return true;
        }

        if (isRF && category === 'balance') {
          return canSeeBalanceTab;
        }

        if (category === 'health') {
          return allBenefitsState.data['health']?.length > 0;
        } else if (category === 'benefits') {
          return Object.keys(allBenefitsState.data).some(
            (cat) => cat !== 'health' && allBenefitsState.data[cat]?.length > 0
          );
        }

        return false;
      });

      setDynamicTabs(filteredTabs);

      if (tabIndex >= filteredTabs.length && filteredTabs.length > 0) {
        setTabIndex(0);
      }
    }
  }, [allBenefitsState.isLoading, allBenefitsState.data, tabIndex, isRF, isUSA, canSeeBalanceTab]);

  const tabsItems = useMemo(() => dynamicTabs.map((tab) => t(tab.title)), [dynamicTabs, t]);

  const backAction = useCallback(() => {
    history.back();
  }, []);

  const handleTabChange = useCallback(
    (index: number) => {
      setTabIndex(index);
      const section = dynamicTabs[index]?.type;
      if (section) {
        TagManagerService.pushEvent(CLICK_ACTION, COLLABORATOR_TABS, { section });
      }
    },
    [dynamicTabs]
  );

  const healthData = useMemo<BenefitsState>(
    () => ({
      data: {
        health: allBenefitsState.data['health'] || []
      },
      isLoading: allBenefitsState.isLoading,
      error: allBenefitsState.error
    }),
    [allBenefitsState]
  );

  const benefitsData = useMemo<BenefitsState>(() => {
    const filteredBenefits = Object.keys(allBenefitsState.data).reduce<ICategorizedBenefits>((acc, category) => {
      if (category !== 'health') {
        acc[category] = allBenefitsState.data[category];
      }
      return acc;
    }, {});

    return {
      data: filteredBenefits,
      isLoading: allBenefitsState.isLoading,
      error: allBenefitsState.error
    };
  }, [allBenefitsState]);

  if (!collaborator) {
    return <Navigate to={COLLABORATORS_HOME_ROUTE} replace />;
  }

  return (
    <div className="collaborator-detail-section w-full">
      <div className={`header ${isScrolled && 'collapsed'}`}>
        <CollaboratorsDetailHero
          collaborator={collaborator}
          planName={planName}
          backAction={backAction}
          hasNovation={rfDataState.data?.hasNovation}
          isFR={isRF}
          loading={rfDataState.isLoading}
        />
        {!isScrolled && (
          <div className="tabs">
            {allBenefitsState.isLoading || tabsItems.length <= 0 || rfDataState.isLoading ? (
              <div className="flex flex-row gap-4 pl-12">
                {COLLABORATOR_DETAIL_TABS.map((tab) => (
                  <Skeleton
                    key={`skeleton-${tab.type}`}
                    className="rounded-4xl"
                    variant="rectangular"
                    height={36}
                    width={134}
                  />
                ))}
              </div>
            ) : (
              <div className="tabbar">
                <BdsSegment items={tabsItems} activeIndex={tabIndex} action={handleTabChange} fullWidth={true} />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="collaborator-detail-tabs-content">
        {allBenefitsState.isLoading || tabsItems.length <= 0 || rfDataState.isLoading ? (
          <Skeleton className="rounded-4xl" variant="rectangular" height={300} />
        ) : (
          <>
            {selectedTab?.type === 'health' && (
              <HealthProtection
                healthData={healthData}
                collaborator={collaborator}
                supportDependents={currentPlan?.supportDependents ?? false}
              />
            )}
            {selectedTab?.type === 'benefits' && <Benefits benefitsData={benefitsData} />}
            {selectedTab?.type === 'contract' && (
              <ContractDetails
                email={collaborator.email}
                currencySalary={currencySalary}
                playerDataState={playerDataState}
                onRetryPlayerData={refetchPlayerData}
              />
            )}
            {selectedTab?.type === 'balance' && <CollaboratorBalance email={collaborator.email} />}
            {selectedTab?.type === 'contact' && (
              <ContactData
                emails={playerDataState.data?.emails ?? []}
                isLoading={playerDataState.isLoading}
                isError={playerDataState.error}
                onRetry={refetchPlayerData}
              />
            )}
            {selectedTab?.type === PLAN_TYPE_FR && (
              <EditRfData
                currencySalary={currencySalary}
                email={collaborator.email}
                planId={Number(`${currentPlan?.id}`)}
                initialData={rfDataState.data}
                isLoading={rfDataState.isLoading}
                isError={rfDataState.error}
                onDataUpdated={refetchData}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CollaboratorDetail;
